import { type ReactNode, useEffect, useState } from "react";

type IRetryableArgs = {
  isRetrying?: boolean;
  maxRetries?: number;
  onRetry: () => void;
};

function useRetryable(
  condition: boolean,
  { isRetrying, maxRetries = 3, onRetry }: IRetryableArgs,
) {
  const [retries, setRetries] = useState(0);
  const [hasRetried, setHasRetried] = useState(false);

  const canRetry = retries < maxRetries;
  const shouldRetry = !condition;

  function retry() {
    if (!canRetry) {
      return;
    }

    onRetry();
    setHasRetried(true);
  }

  useEffect(() => {
    if (hasRetried && !isRetrying) {
      setRetries((retries) => retries + 1);
    }
  }, [isRetrying]);

  return {
    canRetry,
    maxRetries,
    retries,
    retry,
    shouldRetry,
  };
}

export type RetryableRenderProps = ReturnType<typeof useRetryable> & {
  isRetrying?: boolean;
};

type RetryableProps = {
  children: ReactNode | ((props: RetryableRenderProps) => ReactNode);
  condition: boolean;
  fallback?: ReactNode | ((props: RetryableRenderProps) => ReactNode);
  isRetrying?: boolean;
  onRetry: () => void;
};

export function Retryable({
  children,
  fallback,
  onRetry,
  isRetrying,
  condition,
}: RetryableProps) {
  const { canRetry, maxRetries, retries, retry, shouldRetry } = useRetryable(
    condition,
    {
      isRetrying,
      onRetry,
    },
  );

  if (typeof children !== "function") {
    if (!shouldRetry) {
      return children;
    }

    return typeof fallback !== "function"
      ? fallback
      : fallback({
          canRetry,
          isRetrying,
          maxRetries,
          retries,
          retry,
          shouldRetry,
        });
  }

  return children({
    canRetry,
    isRetrying,
    maxRetries,
    retries,
    retry,
    shouldRetry,
  });
}
